import { GridColDef } from "@mui/x-data-grid";
import { EventDefinitionsContext, FormDefinitionsContext, useQueryLookups } from "@ngt/opms";
import { DateTime } from "luxon";
import { useCallback, useContext, useMemo } from "react";
import { PatientQuerySummary } from "../../api/dtos";


const measureText = (text?: string, styleFn?: (ele:HTMLSpanElement) => void) => {

    if (!text) {
        return 0;
    }

    try {
        const measure = document.createElement("span");
        measure.id = 'measure-column-width'
        measure.innerText = text ?? '';
        styleFn?.(measure);
        //measure.style.width = '100%'
        document.body.appendChild(measure);

        const textNode = measure.firstChild;
        const range = document.createRange();
        range.selectNodeContents(textNode as any);
        const width = measure.getClientRects()[0].width;

        measure.remove();

        return width;
    }
    catch {
        return undefined;
    }
}

export const usePatientQueryColumns = (data?: PatientQuerySummary[]) => {    
    const lookups = useQueryLookups();
    const {data: eventDefinitions} = useContext(EventDefinitionsContext);
    const {data: formDefinitions} = useContext(FormDefinitionsContext);
    const getLookupString = useCallback((id?: number, propertyName?: string) => {
        if (!id) {
            return "Unknown";
        }
        
        return lookups.find(lu => lu.propertyName == propertyName)?.items.find(item => item.id == id)?.value;
    }, [lookups]);

    const columns: GridColDef<PatientQuerySummary>[] = useMemo(() => {
        const c: (GridColDef<PatientQuerySummary> | boolean)[] = [  
            {
                field: 'queryType',
                headerName: 'Type',
                type: "singleSelect",
                valueOptions: lookups.find(lu => lu.propertyName == "queryType")?.items.map(x => x.value),
                valueGetter: (_, row) => getLookupString(row.type, "queryType"),
                width: 350
            },
            {
                field: 'eventName',
                headerName: 'Event',
                type: 'singleSelect',
                valueOptions: eventDefinitions?.map(ed => ed.name),
                width: 350
            },
            {
                field: 'formName',
                headerName: 'Form',
                type: 'singleSelect',
                valueOptions: formDefinitions?.map(fd => fd.name),
                width: 350
            },
            {
                field: 'fieldName',
                headerName: 'Field',
                width: 350
            },
            {
                field: 'status',
                headerName: 'Status',
                type: "singleSelect",
                valueOptions: lookups.find(lu => lu.propertyName == "queryStatus")?.items.map(x => x.value),
                valueGetter: (_, row) => getLookupString(row.status, "queryStatus"),
                width: 350
            },
            {
                field: "title",
                headerName: "Title",
                width: 350
            },
            {
                field: "enteredBy",
                headerName: "Entered By",
                width: 350
            },
            {
                field: "dateEntered",
                headerName: "Date Entered",
                type: "date",
                valueGetter: (_, row) => row.dateEntered ? new Date(row.dateEntered) : undefined,
                valueFormatter: (_, row) => row.dateEntered ? DateTime.fromISO(row.dateEntered).toFormat("dd-MMM-yyyy") : undefined,
                width: 350
            }
        ];

        return c.filter(column => !!column) as GridColDef<PatientQuerySummary>[];
    }, [lookups, getLookupString, eventDefinitions, formDefinitions]);

    const resizedColumns = useMemo(() => {
        return columns.map(col => {
            const largestColumnValue = data?.reduce((a, b) => {
                const value = col.valueGetter ?
                    col.valueGetter(undefined as never, b, undefined as any, undefined as any) :
                    col.field ?
                        (b as any)[col.field] :
                        undefined;

                const formattedValue = col.valueFormatter ?
                    col.valueFormatter(value as never, b, undefined as any, undefined as any) :
                    value;

                if (formattedValue && formattedValue.length > a.length) {
                    return formattedValue
                }

                return a;
            }, "")

            let width = measureText(largestColumnValue, (measure) => {
                measure.style.fontSize = '0.9rem';
            }) ?? 0;

            let headerWidth = measureText(col.headerName, (measure) => {
                measure.style.fontSize = '0.9rem';
            }) ?? 0;
        
            headerWidth += 60;

            let calculatedWidth = Math.max(width, headerWidth) + //Text width;
                20; //padding width
            
            if (col.width && calculatedWidth > col.width) {
                calculatedWidth = col.width;
            }
        
        
            return {
                ...col,
                width: calculatedWidth
            }
        });
        
    },[columns, data])

    return resizedColumns;
};